import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Shuffle, Play, Download, RefreshCw, Settings2, ShieldAlert, EyeOff, Info, X } from "lucide-react";

const COLUMN_TYPES = ["text", "number", "date", "boolean"];
const TYPE_COLORS = { text: "#4B5563", number: "#2563EB", date: "#7C3AED", boolean: "#059669" };
const TYPE_BG = { text: "#F3F4F6", number: "#DBEAFE", date: "#EDE9FE", boolean: "#D1FAE5" };

const InfoTooltip = ({ text }) => (
  <div className="relative inline-block group align-middle ml-1.5 cursor-help">
    <Info size={13} className="text-gray-400 hover:text-[#006080]" />
    <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2.5 bg-gray-900 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none leading-relaxed">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
    </div>
  </div>
);

// PII panel component
function PiiPanel({ uploadId, onClose }) {
  const [detecting, setDetecting] = useState(false);
  const [masking, setMasking] = useState(false);
  const [piiResult, setPiiResult] = useState(null);
  const [selectedCols, setSelectedCols] = useState([]);
  const [strategy, setStrategy] = useState("redact");
  const [maskDone, setMaskDone] = useState(null);

  const detect = async () => {
    setDetecting(true); setPiiResult(null); setMaskDone(null);
    try {
      const res = await client.get(`/pii/detect/${uploadId}`);
      setPiiResult(res.data);
      setSelectedCols(res.data.pii_columns.map(c => c.column));
    } catch (e) {
      console.error(e);
    } finally { setDetecting(false); }
  };

  const mask = async () => {
    if (!selectedCols.length) return;
    setMasking(true);
    try {
      const res = await client.post(`/pii/mask/${uploadId}`, { columns: selectedCols, strategy });
      setMaskDone(res.data);
    } catch (e) {
      console.error(e);
    } finally { setMasking(false); }
  };

  const STRATEGY_LABELS = { redact: "[REDACTED]", hash: "SHA-256 hash", partial: "Par**al" };
  const SEV_COLOR = { email: "#DC2626", phone: "#D97706", ssn: "#7C2D12", credit_card: "#9333EA", ip_address: "#1D4ED8", passport: "#059669" };

  return (
    <div className="bg-white border border-amber-200 rounded-xl shadow-lg p-5 mb-5">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldAlert size={18} className="text-amber-600" />
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">PII Detection & Masking</h3>
            <p className="text-xs text-gray-500">Scan your data for personal information and optionally mask it before sending to external APIs.</p>
          </div>
          <InfoTooltip text="Scans up to 500 rows for emails, phone numbers, SSNs, credit cards, IP addresses, and passport numbers. You can then mask selected columns using your preferred strategy before executing." />
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
      </div>

      <Button variant="outline" size="sm" onClick={detect} disabled={detecting} className="mb-4">
        <ShieldAlert size={13} className="mr-1.5 text-amber-600" />
        {detecting ? "Scanning..." : "Scan for PII"}
      </Button>

      {piiResult && (
        <div className="space-y-3">
          {piiResult.clean ? (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
              ✅ No PII detected in {piiResult.rows_scanned} rows scanned.
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-500">{piiResult.rows_scanned} rows scanned · {piiResult.pii_columns.length} column(s) flagged</p>
              {piiResult.pii_columns.map(col => (
                <div key={col.column} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input type="checkbox" checked={selectedCols.includes(col.column)}
                      onChange={e => setSelectedCols(prev => e.target.checked ? [...prev, col.column] : prev.filter(c => c !== col.column))} />
                    <span className="font-semibold text-xs text-gray-800">{col.column}</span>
                    {col.pii_types.map(pt => (
                      <span key={pt.type} className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                        style={{ background: `${SEV_COLOR[pt.type] || "#6B7280"}15`, color: SEV_COLOR[pt.type] || "#6B7280" }}>
                        {pt.label} ({pt.count})
                      </span>
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-400 ml-5">e.g. {col.pii_types[0]?.examples?.join(", ")}</p>
                </div>
              ))}
              {selectedCols.length > 0 && (
                <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                  <Select value={strategy} onValueChange={setStrategy}>
                    <SelectTrigger className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STRATEGY_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}><span className="font-mono">{v}</span></SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={mask} disabled={masking}>
                    <EyeOff size={13} className="mr-1.5" />
                    {masking ? "Masking..." : `Mask ${selectedCols.length} column(s)`}
                  </Button>
                </div>
              )}
              {maskDone && (
                <div className="p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                  ✅ Masked {maskDone.masked_rows} rows in [{maskDone.columns.join(", ")}] using {maskDone.strategy}.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function StagingTablePage() {
  const { uploadId } = useParams();
  const navigate = useNavigate();
  const [upload, setUpload] = useState(null);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState([]);
  const [editingTypes, setEditingTypes] = useState(false);
  const [savingTypes, setSavingTypes] = useState(false);
  const [showPii, setShowPii] = useState(false);

  useEffect(() => {
    loadUpload();
  }, [uploadId]);

  useEffect(() => {
    if (upload) loadRows();
  }, [upload, page]);

  const loadUpload = async () => {
    try {
      const res = await client.get(`/files/${uploadId}`);
      setUpload(res.data);
      setColumns(res.data.columns || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadRows = async () => {
    setLoading(true);
    try {
      const res = await client.get(`/staging/${uploadId}/rows`, { params: { page, per_page: 50 } });
      setRows(res.data.rows);
      setTotalPages(res.data.total_pages);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const saveColumnTypes = async () => {
    setSavingTypes(true);
    try {
      await client.put(`/files/${uploadId}/columns`, { columns });
      setEditingTypes(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSavingTypes(false);
    }
  };

  const updateColType = (idx, newType) => {
    const updated = columns.map((c, i) => i === idx ? { ...c, type: newType } : c);
    setColumns(updated);
  };

  // Build the full display column list: schema columns FIRST, then any extra
  // columns found in the actual row data (e.g., resp_* columns added post-execution)
  const schemaColNames = columns.map(c => c.name);
  const extraColNames = rows.length > 0
    ? Object.keys(rows[0]).filter(k =>
        !["upload_id", "row_index"].includes(k) && !schemaColNames.includes(k)
      )
    : [];
  const displayColumns = schemaColNames.length > 0
    ? [...schemaColNames, ...extraColNames]
    : Object.keys(rows[0] || {}).filter(k => !["upload_id", "row_index"].includes(k));

  // Column metadata lookup (for type badge)
  const colMeta = (colName) =>
    columns.find(c => c.name === colName) ||
    (colName.startsWith("resp_") ? { name: colName, type: "text" } : null);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 bg-white border-b border-gray-200 flex items-center gap-3 flex-shrink-0">
        <button onClick={() => navigate("/upload")} className="text-gray-400 hover:text-gray-600">
          <ChevronLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold text-gray-900 truncate">{upload?.filename}</h1>
          <p className="text-xs text-gray-400">{total.toLocaleString()} rows · {displayColumns.length} columns {extraColNames.length > 0 ? `(+${extraColNames.length} response)` : ""}</p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="border-amber-300 text-amber-600 hover:bg-amber-50"
            onClick={() => setShowPii(!showPii)}
            data-testid="pii-btn"
          >
            <ShieldAlert size={14} className="mr-1.5" /> PII
            <InfoTooltip text="Detect and mask personally identifiable information (emails, phones, SSNs, credit cards) before sending data to external APIs." />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-gray-200 text-gray-600"
            onClick={() => setEditingTypes(!editingTypes)}
            data-testid="edit-types-btn"
          >
            <Settings2 size={14} className="mr-1.5" /> Column Types
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-[#006080] text-[#006080] hover:bg-[#e6f2f5]"
            onClick={() => navigate(`/transform/${uploadId}`)}
            data-testid="transform-btn"
          >
            <Shuffle size={14} className="mr-1.5" /> Transform
          </Button>
          <Button
            size="sm"
            className="bg-[#006080] hover:bg-[#004d66] text-white"
            onClick={() => navigate(`/execute/${uploadId}`)}
            data-testid="execute-btn"
          >
            <Play size={14} className="mr-1.5" /> Execute
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-gray-400"
            onClick={loadRows}
            title="Refresh"
          >
            <RefreshCw size={14} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-gray-400"
            onClick={() => {
              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/staging/${uploadId}/export-xlsx`, "_blank");
            }}
            title="Export as XLSX (includes response columns)"
          >
            <Download size={14} />
          </Button>
        </div>
      </div>

      {/* PII Panel */}
      {showPii && <div className="px-6 pt-4"><PiiPanel uploadId={uploadId} onClose={() => setShowPii(false)} /></div>}

      {/* Column types editor */}
      {editingTypes && (
        <div className="px-6 py-3 bg-[#F0F8FA] border-b border-[#006080]/20 flex-shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-[#006080] uppercase tracking-wider">Edit Column Types</span>
            <Button size="sm" className="h-6 px-2 text-xs bg-[#006080] text-white" onClick={saveColumnTypes} disabled={savingTypes}>
              {savingTypes ? "Saving..." : "Save"}
            </Button>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setEditingTypes(false)}>Cancel</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {columns.map((col, idx) => (
              <div key={col.name} className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-md px-2 py-1">
                <span className="text-xs font-medium text-gray-700 max-w-[120px] truncate">{col.name}</span>
                <Select value={col.type} onValueChange={(v) => updateColType(idx, v)}>
                  <SelectTrigger className="h-5 w-[80px] text-xs border-0 bg-transparent p-0 focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLUMN_TYPES.map(t => (
                      <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-6 h-6 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <table className="w-full data-table text-sm" data-testid="staging-table">
            <thead className="sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left w-12 text-gray-400">#</th>
                {displayColumns.map((col) => {
                  const meta = colMeta(col);
                  const isResp = col.startsWith("resp_");
                  return (
                    <th key={col} className={`px-3 py-2 text-left whitespace-nowrap ${isResp ? "bg-amber-50" : ""}`}>
                      <div className="flex items-center gap-1.5">
                        <span>{col}</span>
                        {meta && (
                          <span
                            className="text-[10px] px-1 rounded-sm font-semibold"
                            style={{
                              background: isResp ? "#FEF3C7" : (TYPE_BG[meta.type] || "#F3F4F6"),
                              color: isResp ? "#92400E" : (TYPE_COLORS[meta.type] || "#4B5563"),
                            }}
                          >
                            {isResp ? "resp" : meta.type}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} data-testid={`row-${ri}`}>
                  <td className="px-3 py-2 text-gray-300 text-xs w-12">
                    {((page - 1) * 50) + ri + 1}
                  </td>
                  {displayColumns.map(col => (
                    <td key={col} className="px-3 py-2">
                      <span title={String(row[col] ?? "")}>
                        {row[col] === null || row[col] === undefined ? (
                          <span className="text-gray-300 italic text-xs">null</span>
                        ) : String(row[col])}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="px-6 py-3 bg-white border-t border-gray-200 flex items-center justify-between flex-shrink-0">
        <span className="text-xs text-gray-400">
          {total.toLocaleString()} total rows · Page {page} of {totalPages}
        </span>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
            <ChevronLeft size={14} />
          </Button>
          <span className="px-3 flex items-center text-xs text-gray-600">{page} / {totalPages}</span>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page">
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
